import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Fullscreen image lightbox overlay.
 *
 * ## Usage
 * ```html
 * <!-- Thumbnail that opens the lightbox on click -->
 * <img [src]="photoUrl" (click)="lightboxOpen = true" style="cursor: zoom-in" />
 *
 * <!-- Lightbox (rendered in a portal-style fixed overlay) -->
 * <abs-lightbox
 *     [src]="photoUrl"
 *     [open]="lightboxOpen"
 *     (closed)="lightboxOpen = false"
 * ></abs-lightbox>
 * ```
 *
 * Clicking anywhere on the overlay, or pressing Escape, closes the lightbox.
 * The host element has no size of its own — it only renders content when `open` is true.
 */
@Component({
    standalone: true,
    imports: [CommonModule],
    selector: 'abs-lightbox',
    template: `
        @if (open && src) {
            <div class="overlay" (click)="close()" role="dialog" aria-modal="true">
                <img [src]="src" [alt]="alt" class="image" />
                <button class="closeBtn" (click)="close()" aria-label="Close">✕</button>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: contents;
            }
            .overlay {
                position: fixed;
                inset: 0;
                z-index: 99999;
                background: rgba(0, 0, 0, 0.92);
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: zoom-out;
                /* Safe-area padding for notched devices */
                padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
            }
            .image {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
                border-radius: 4px;
                cursor: zoom-out;
                box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6);
            }
            .closeBtn {
                position: absolute;
                top: max(1rem, env(safe-area-inset-top, 1rem));
                right: max(1rem, env(safe-area-inset-right, 1rem));
                width: 2.25rem;
                height: 2.25rem;
                border-radius: 50%;
                border: none;
                background: rgba(255, 255, 255, 0.15);
                color: #fff;
                font-size: 1rem;
                line-height: 1;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                backdrop-filter: blur(4px);
            }
            .closeBtn:hover {
                background: rgba(255, 255, 255, 0.25);
            }
        `
    ]
})
export class LightboxComponent {
    /** URL of the image to display. */
    @Input() src: string | null = null;
    /** Alt text for the lightbox image. */
    @Input() alt = '';
    /** Whether the lightbox is currently visible. */
    @Input() open = false;
    /** Emitted when the user closes the lightbox (click or Escape). */
    @Output() closed = new EventEmitter<void>();

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.open) this.close();
    }

    close(): void {
        this.closed.emit();
    }
}
